import { useCallback, useState } from 'react';
import { useNavigate } from '@remix-run/react';
import type { LeaveBuildHubMutation } from 'generated/graphql';
import { LEAVE_BUILD_HUB_API_ROUTE } from '~/routes/remix-api.app.build-hub-leave';

interface UseLeaveBuildHubOptions {
    onSuccess?: () => void;
    shouldNavigateOnSuccess?: boolean;
}

interface UseLeaveBuildHubReturn {
    leaveBuildHub: (buildHubUrl: string) => Promise<void>;
    isLeaving: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Reusable hook for leaving a BuildHub project
 */
export function useBuildHubLeave(
    options: UseLeaveBuildHubOptions = {}
): UseLeaveBuildHubReturn {
    const { onSuccess, shouldNavigateOnSuccess = false } = options;
    const navigate = useNavigate();

    const [isLeaving, setIsLeaving] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const leaveBuildHub = useCallback(
        async (id: string) => {
            if (!id) throw new Error('Invalid build hub ID provided');

            setIsLeaving(true);
            setError(null);
            setIsSuccess(false);

            try {
                const formData = new FormData();
                formData.set('id', id);

                // using await fetch instead of fetcher.submit because immediately after submitting the page loader would throws 403
                // we have to get the user out of here before that happens
                const response = await fetch(LEAVE_BUILD_HUB_API_ROUTE, {
                    method: 'POST',
                    body: formData,
                });

                const data: LeaveBuildHubMutation = await response.json();
                const result = data.buildHubLeaveProject;

                if (result && result.__typename === 'BuildHubCollaborator') {
                    setIsSuccess(true);
                    onSuccess?.();
                    if (shouldNavigateOnSuccess) {
                        navigate('/app/my-projects');
                    }
                } else {
                    throw new Error(
                        result?.Message || 'Failed to leave project.'
                    );
                }
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : 'Unexpected error'
                );
            } finally {
                setIsLeaving(false);
            }
        },
        [navigate, onSuccess, shouldNavigateOnSuccess]
    );

    return {
        leaveBuildHub,
        isLeaving,
        error,
        isSuccess,
        isIdle: !isLeaving,
    };
}
